import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAIFilingPayment, isAIFilingPlan } from "../hooks/useAIFilingPayment";

const MAX_POLLS = 10;
const POLL_INTERVAL_MS = 3000;

const PaymentCallbackPage = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | success | failed | timeout
  const [message, setMessage] = useState("");

  // Phase 2 — bridge hook
  const { createAISession } = useAIFilingPayment();

  useEffect(() => {
    const paymentId = localStorage.getItem("pending_payment_id");
    const requestId = localStorage.getItem("pending_request_id");

    if (!paymentId || !requestId) {
      navigate("/dashboard", { replace: true });
      return;
    }

    let pollCount = 0;

    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${paymentId}`);
        const { status: payStatus } = res.data;

        if (payStatus === "completed") {
          localStorage.removeItem("pending_payment_id");
          localStorage.removeItem("pending_request_id");
          setStatus("success");

          // Phase 2 — check if this is an AI filing plan
          // If yes, create a CF Worker session and redirect to /ai-filing
          // If no, redirect to the request page as normal (Phase 1 flow)
          try {
            const requestRes = await api.get(`/requests/${requestId}`);
            const filingRequest = requestRes.data;

            if (isAIFilingPlan(filingRequest)) {
              // AI Filing plan — hand off to Phase 2
              await createAISession(filingRequest, res.data.amount);
              return; // createAISession handles redirect
            }
          } catch {
            // If fetching request fails, fall through to normal Phase 1 redirect
          }

          // Phase 1 — normal redirect to request page
          setTimeout(() => navigate(`/request/${requestId}`, { replace: true }), 2500);
          return;
        }

        if (payStatus === "failed") {
          localStorage.removeItem("pending_payment_id");
          localStorage.removeItem("pending_request_id");
          setStatus("failed");
          setMessage(
            "Your payment could not be processed. No amount has been deducted. Please try again."
          );
          return;
        }

        // Still pending — keep polling
        pollCount++;
        if (pollCount >= MAX_POLLS) {
          setStatus("timeout");
          setMessage(
            "Payment is taking longer than expected. If your amount was deducted, it will be reflected shortly. Check your dashboard or contact support."
          );
          return;
        }

        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        pollCount++;
        if (pollCount >= MAX_POLLS) {
          setStatus("timeout");
          setMessage(
            "Could not verify payment status. Please check your dashboard or contact support@mooltax.com."
          );
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    // Start polling after a short delay to let webhook arrive first
    setTimeout(poll, 2000);
  }, [navigate]);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{ backgroundColor: "var(--secondary)" }}
    >
      <div
        className="bg-white rounded-xl border p-10 w-full max-w-md text-center"
        style={{ borderColor: "var(--border)" }}
      >
        {status === "checking" && (
          <>
            <div className="spinner mx-auto mb-6" style={{ width: 40, height: 40 }} />
            <h1
              className="text-2xl font-bold mb-2"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              Confirming your payment...
            </h1>
            <p className="opacity-60 text-sm">
              Please wait. Do not close this page or press back.
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl"
              style={{ backgroundColor: "#dcfce7" }}
            >
              ✓
            </div>
            <h1
              className="text-2xl font-bold mb-2"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              Payment successful!
            </h1>
            <p className="opacity-70 mb-2">
              Your payment has been confirmed and your tax filing case is now active.
            </p>
            <p className="opacity-50 text-sm">Redirecting you now...</p>
          </>
        )}

        {status === "failed" && (
          <>
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl"
              style={{ backgroundColor: "#fee2e2" }}
            >
              ✗
            </div>
            <h1
              className="text-2xl font-bold mb-2"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              Payment failed
            </h1>
            <p className="opacity-70 mb-6 text-sm leading-6">{message}</p>
            <button
              onClick={() => {
                localStorage.removeItem("pending_payment_id");
                localStorage.removeItem("pending_request_id");
                navigate("/plans");
              }}
              className="btn-primary px-6 py-3 rounded-lg w-full mb-3"
            >
              Retry Payment
            </button>
            <button
              onClick={() => navigate("/dashboard")}
              className="text-sm opacity-60 underline"
            >
              Go to Dashboard
            </button>
          </>
        )}

        {status === "timeout" && (
          <>
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl"
              style={{ backgroundColor: "#fef9c3" }}
            >
              ⏱
            </div>
            <h1
              className="text-2xl font-bold mb-2"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              Payment status pending
            </h1>
            <p className="opacity-70 mb-6 text-sm leading-6">{message}</p>
            <button
              onClick={() => navigate("/dashboard")}
              className="btn-primary px-6 py-3 rounded-lg w-full mb-3"
            >
              Go to Dashboard
            </button>
            <a
              href="mailto:support@mooltax.com"
              className="text-sm opacity-60 underline block"
            >
              Contact Support
            </a>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentCallbackPage;